import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Typography,
  Button,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material'
import { Delete as DeleteIcon, Search as SearchIcon } from '@mui/icons-material'
import { cardsApi } from '@/api/cardsApi'
import type { Clue } from '@/types'

const CLUE_TEXT_MAX = 500

interface CardCluesProps {
  cardId: number
  wordId: number
  clues: Clue[]
  onClueAdded?: (clue: Clue) => void
  onClueDeleted?: (clueId: number) => void
}

export function CardClues({
  cardId,
  wordId,
  clues,
  onClueAdded,
  onClueDeleted,
}: CardCluesProps) {
  const { t } = useTranslation()
  const [newClueText, setNewClueText] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const [deleteLoadingId, setDeleteLoadingId] = useState<number | null>(null)
  const [findLoading, setFindLoading] = useState(false)
  const [externalClues, setExternalClues] = useState<Clue[]>([])
  const [error, setError] = useState<string | null>(null)

  const handleAddClue = async () => {
    const text = newClueText.trim()
    if (!text) {
      setError(t('cards.validation.clue_text_required'))
      return
    }
    if (text.length > CLUE_TEXT_MAX) {
      setError(t('cards.validation.clue_text_max'))
      return
    }
    setError(null)
    setAddLoading(true)
    try {
      const clue = await cardsApi.addClue(cardId, text)
      setNewClueText('')
      onClueAdded?.(clue)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message || t('cards.errors.load_failed'))
    } finally {
      setAddLoading(false)
    }
  }

  const handleDeleteClue = async (clueId: number) => {
    if (!window.confirm(t('common.delete') + '?')) return
    setDeleteLoadingId(clueId)
    try {
      await cardsApi.deleteClue(cardId, clueId)
      onClueDeleted?.(clueId)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message || t('cards.errors.load_failed'))
    } finally {
      setDeleteLoadingId(null)
    }
  }

  const handleFindFromOthers = async () => {
    setFindLoading(true)
    setError(null)
    setExternalClues([])
    try {
      const list = await cardsApi.getWordClues(wordId)
      setExternalClues(list)
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message || t('cards.errors.load_failed'))
    } finally {
      setFindLoading(false)
    }
  }

  const handleAddExternalClue = async (text: string) => {
    setAddLoading(true)
    setError(null)
    try {
      const clue = await cardsApi.addClue(cardId, text)
      onClueAdded?.(clue)
      setExternalClues((prev) => prev.filter((c) => c.text !== text))
    } catch (err) {
      const e = err as { response?: { data?: { message?: string } } }
      setError(e.response?.data?.message || t('cards.errors.load_failed'))
    } finally {
      setAddLoading(false)
    }
  }

  const currentClueTexts = new Set(clues.map((c) => c.text))

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="h6">{t('cards.clues')}</Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <List dense>
        {clues.map((clue) => (
          <ListItem
            key={clue.id}
            secondaryAction={
              <IconButton
                edge="end"
                size="small"
                onClick={() => handleDeleteClue(clue.id)}
                disabled={deleteLoadingId === clue.id}
              >
                {deleteLoadingId === clue.id ? (
                  <CircularProgress size={20} />
                ) : (
                  <DeleteIcon />
                )}
              </IconButton>
            }
          >
            <ListItemText primary={clue.text} />
          </ListItem>
        ))}
      </List>

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <TextField
          fullWidth
          size="small"
          multiline
          minRows={1}
          maxRows={6}
          label={t('cards.clue_text')}
          value={newClueText}
          onChange={(e) => setNewClueText(e.target.value)}
          inputProps={{ maxLength: CLUE_TEXT_MAX }}
          helperText={`${newClueText.length}/${CLUE_TEXT_MAX}`}
          sx={{
            '& .MuiInputBase-root': { minHeight: 40, alignItems: 'flex-start' },
            '& .MuiInputBase-input': { minHeight: 22 },
          }}
        />
        <Button
          variant="contained"
          size="small"
          onClick={handleAddClue}
          disabled={addLoading || !newClueText.trim()}
          sx={{ height: 40, minHeight: 40 }}
        >
          {t('cards.add')}
        </Button>
      </Box>

      <Button
        variant="outlined"
        startIcon={findLoading ? <CircularProgress size={20} /> : <SearchIcon />}
        onClick={handleFindFromOthers}
        disabled={findLoading}
      >
        {t('cards.find_clues_from_other_students')}
      </Button>

      {externalClues.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            {t('cards.messages.clues_found', { count: externalClues.length })}
          </Typography>
          <List dense>
            {externalClues.map((clue) => {
              const alreadyAdded = currentClueTexts.has(clue.text)
              return (
                <ListItem
                  key={clue.id}
                  secondaryAction={
                    !alreadyAdded && (
                      <Button
                        size="small"
                        onClick={() => handleAddExternalClue(clue.text)}
                        disabled={addLoading}
                      >
                        {t('cards.add_clue')}
                      </Button>
                    )
                  }
                >
                  <ListItemText
                    primary={clue.text}
                    secondary={alreadyAdded ? t('cards.messages.clue_added') : null}
                  />
                </ListItem>
              )
            })}
          </List>
        </Box>
      )}
    </Box>
  )
}
